/**
 * Network Score level name (docs/features/future-roadmap/network-score.md, sections 2 and 5).
 * `ScoreLevel` rows — including the level's name, points threshold and badge colour — are
 * admin-editable (network-score.md, section 5), so this is deliberately an open `string` rather
 * than a fixed union: the API is the only source of truth for level names and thresholds, and a
 * future rename or re-threshold needs no frontend code change. Styling must key off
 * {@link getLevelColours}'s `badgeColourKey`, never off this name.
 */
export type ScoreLevelName = string;

interface IScoreLevelColours {
    /** Solid fill for the level disc (Network Score surfaces only) — the level's representative colour. */
    solidBg: string;
    solidText: string;
    /** Tinted pill for the level tag/badge, shown next to a name — same colour family as `solidBg`. */
    tagBg: string;
    tagText: string;
}

/** Shown for a level with no recognised `badgeColourKey` — kept neutral so nothing visually breaks. */
const DEFAULT_LEVEL_COLOURS: IScoreLevelColours = {
    solidBg: 'var(--grey-500)', solidText: 'var(--white)', tagBg: 'var(--grey-200)', tagText: 'var(--grey-700)',
};

/**
 * Maps the API's `badgeColourKey` (`ScoreLevel.BadgeColourKey`, admin-editable per
 * network-score.md section 5) to the brand's monotonic single-family badge colours: grey → cyan →
 * cyan-dark → violet → violet-dark → gold. Every value reuses an existing theme/token — see
 * `odyssey.app/src/theme/tokens/colors.css`. Keyed by the backend's literal seed values (e.g.
 * `'cyanDark'`), not the level name, so admins can rename or re-order levels without touching this
 * file — only a genuinely new colour key needs an entry added here.
 */
export const BADGE_COLOUR_KEY_COLOURS: Record<string, IScoreLevelColours> = {
    grey600: DEFAULT_LEVEL_COLOURS,
    cyanMain: { solidBg: 'var(--cyan-main)', solidText: 'var(--white)', tagBg: 'var(--cyan-lighter)', tagText: 'var(--cyan-main)' },
    cyanDark: { solidBg: 'var(--blue)', solidText: 'var(--white)', tagBg: 'color-mix(in srgb, var(--blue) 10%, white)', tagText: 'var(--blue)' },
    violetMain: { solidBg: 'var(--violet-main)', solidText: 'var(--white)', tagBg: 'var(--violet-lighter)', tagText: 'var(--violet-main)' },
    violetDark: { solidBg: 'var(--violet-dark)', solidText: 'var(--white)', tagBg: 'var(--violet-lighter)', tagText: 'var(--violet-dark)' },
    levelGold: { solidBg: 'var(--app-warning)', solidText: 'var(--ink)', tagBg: 'var(--app-warning-lighter)', tagText: 'var(--app-warning-dark)' },
};

/**
 * Resolves a level's display colours from its `badgeColourKey` (the only admin-configurable,
 * API-sourced source of truth), falling back to {@link DEFAULT_LEVEL_COLOURS} when it's missing or
 * not yet recognised — e.g. other members' levels, where no badge colour key is fetched, or a
 * brand-new key an admin just added. The level name itself never drives colour.
 */
export const getLevelColours = (badgeColourKey?: string | null): IScoreLevelColours =>
    (badgeColourKey && BADGE_COLOUR_KEY_COLOURS[badgeColourKey]) || DEFAULT_LEVEL_COLOURS;

/**
 * A single "next level" habit gate as returned by the API. Mirrors `NextLevelHabitGateResponse`
 * (Odyssey.API/Responses/Participant/MyNetworkScoreSummaryResponse.cs) field-for-field.
 * `habitGate` is a pre-formatted label (already includes the target, e.g. "Follow-up rate ≥ 40%");
 * `currentValue`/`targetValue` are raw decimals — a 0-1 fraction for rate gates, a plain count for
 * "warm intro" gates — so the UI can both format them for display and decide whether the gate is met.
 */
export interface INextLevelHabitGateResponse {
    habitGate: string;
    currentValue: number;
    targetValue: number;
}

/**
 * A level-up the user hasn't acknowledged yet (hasn't seen the celebration modal for) — mirrors
 * `UnacknowledgedLevelUpResponse` (Odyssey.API/Responses/Participant) field-for-field. Drives the
 * web Level-up dialog (network-score.md, sections 8, 11 and 12), fetched via
 * `NetworkScoreService.getUnacknowledgedLevelUp()` — a standalone endpoint, not part of
 * {@link IMyNetworkScoreSummaryResponse}, since the dialog is mounted app-wide rather than only on
 * the Network page. The lesson title comes straight from the backend's `Content/Lessons/en-GB.json`
 * content file rather than being duplicated here.
 */
export interface IUnacknowledgedLevelUpResponse {
    toLevelName: ScoreLevelName;
    toLevelNumber: number;
    badgeColourKey: string;
    pointsThreshold: number;
    rollingWindowDays: number;
    lessonTitle: string;
    hasHabitRequirement: boolean;
}

/**
 * Mirrors `MyNetworkScoreSummaryResponse` (Odyssey.API/Responses/Participant) field-for-field —
 * the response for `GET participant/network-score/me` (network-score.md, section 11). Powers the
 * Network Score card only — see {@link IUnacknowledgedLevelUpResponse} for the separate Level-up
 * check.
 */
export interface IMyNetworkScoreSummaryResponse {
    levelName: ScoreLevelName;
    levelNumber: number;
    /** Admin-editable colour key for the level's badge/disc — see {@link getLevelColours}. */
    badgeColourKey?: string;
    windowPoints: number;
    rollingWindowDays: number;
    nextLevelName: ScoreLevelName;
    nextLevelThreshold: number;
    nextLevelHabitGates: INextLevelHabitGateResponse[];
}

/**
 * The API reports the current level as its own "next level" once there's nowhere higher to go
 * (see NetworkScoreService.GetMyNetworkScoreSummaryAsync) — that equality is the top-level signal.
 */
export const isAtTopLevel = (summary: IMyNetworkScoreSummaryResponse): boolean => summary.levelName === summary.nextLevelName;

/** Progress towards the next level's points threshold — the only numeric gate the API exposes. */
export const getNextLevelProgressPercent = (summary: IMyNetworkScoreSummaryResponse): number => {
    if (isAtTopLevel(summary) || summary.nextLevelThreshold <= 0) return 100;
    return Math.round(Math.min(1, summary.windowPoints / summary.nextLevelThreshold) * 100);
};

/** Whether the user already meets a given next-level habit gate — drives the met/unmet icon. */
export const isHabitGateMet = (gate: INextLevelHabitGateResponse): boolean => gate.currentValue >= gate.targetValue;

/**
 * Formats a habit gate's current value for display. "Warm intro" gates are plain counts; every
 * other gate is a 0-1 rate, formatted as a percentage to one decimal place (mirrors the backend's
 * `FormatPercent` helper in NetworkScoreService.cs).
 */
const formatHabitGateAmount = (gate: INextLevelHabitGateResponse, value: number): string => {
    if (/warm intro/i.test(gate.habitGate)) return `${Math.round(value * 10) / 10}`;
    return `${Math.round(value * 1000) / 10}%`;
};

export const formatHabitGateValue = (gate: INextLevelHabitGateResponse): string => formatHabitGateAmount(gate, gate.currentValue);

/**
 * One-line "where you are against the next gate" caption, e.g. "Follow-up rate 38% → 40% for
 * Connector". Uses the first gate not yet met; null when there is nothing left to reach.
 */
export const getNextGateCaption = (summary: IMyNetworkScoreSummaryResponse): string | null => {
    if (isAtTopLevel(summary)) return null;
    const gate = summary.nextLevelHabitGates.find((g) => !isHabitGateMet(g));
    if (!gate) return null;
    const label = gate.habitGate.split(/[≥>]/)[0].trim();
    return `${label} ${formatHabitGateValue(gate)} → ${formatHabitGateAmount(gate, gate.targetValue)} for ${summary.nextLevelName}`;
};


/**
 * Real seeded rule points (Shared.Data/Seeding/ScoreRuleSeed.cs) used as a display fallback only —
 * every pending item returned by the API already carries its own `followUpPoints`/`reconnectPoints`.
 */
export const SCORE_RULE_POINTS = {
    followUp: 20,
    counterpartyReply: 10,
    reconnect: 15,
} as const;

/**
 * A connection whose follow-up window is still open — mirrors `PendingFollowUpResponse`
 * (Odyssey.API/Responses/Participant) field-for-field.
 */
export interface IPendingFollowUpResponse {
    otherUserId: string;
    otherUserFirstName: string;
    otherUserLastName: string;
    otherUserScoreLevel?: ScoreLevelName | null;
    otherUserImage?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    eventName?: string | null;
    remainingFollowUpDays: number;
    followUpPoints: number;
}

/**
 * A connection that has gone quiet — mirrors `PendingReconnectResponse`
 * (Odyssey.API/Responses/Participant) field-for-field.
 */
export interface IPendingReconnectResponse {
    otherUserId: string;
    otherUserFirstName: string;
    otherUserLastName: string;
    otherUserScoreLevel?: ScoreLevelName | null;
    otherUserImage?: string | null;
    jobTitle?: string | null;
    company?: string | null;
    eventName?: string | null;
    goneQuietDays: number;
    reconnectPoints: number;
}

/**
 * Where the signed-in user stands with one connection's first follow-up — mirrors
 * `FollowUpStatusResponse` (Odyssey.API/Responses/Participant) field-for-field, the response for
 * `GET participant/score/me/follow-ups/{otherUserId}`. `pending`: nothing sent yet and the window
 * is open; `sent`: the first message is out and a reply inside the window pays both people;
 * `none`: nothing to show.
 */
export type FollowUpStatusKind = 'none' | 'pending' | 'sent' | 'reconnect';

export interface IFollowUpStatusResponse {
    status: FollowUpStatusKind;
    eventName?: string | null;
    remainingFollowUpDays: number;
    followUpWindowDays: number;
    followUpPoints: number;
    counterpartyReplyPoints: number;
    /** 0-1 fraction, or null while there are too few connections to judge. */
    followUpRate?: number | null;
    /** Only meaningful when `status` is `reconnect`: the conversation has been quiet for this long. */
    goneQuietDays?: number;
    reconnectQuietDays?: number;
    reconnectPoints?: number;
}

/**
 * Mirrors `MyNetworkScorePendingFollowUpsResponse` (Odyssey.API/Responses/Participant)
 * field-for-field — the response for `GET participant/score/me/pending-follow-ups` (network-score.md,
 * section 11). Drives the web "Follow-ups waiting" paper (section 8).
 */
export interface IMyNetworkScorePendingFollowUpsResponse {
    followUpWindowDays: number;
    reconnectQuietDays: number;
    pendingFollowUpResponses: IPendingFollowUpResponse[];
    pendingReconnectResponses: IPendingReconnectResponse[];
}

/**
 * One habit in "Your habits" — mirrors `NetworkScoreHabitResponseModel`
 * (Shared.Services/Models/NetworkScoreSectionModels.cs). `kind` says how to read `value`: a 0-1
 * `rate`, a number of `weeks` out of `maximum`, or a plain `count`. A rate without enough
 * qualifying events has `hasEnoughData: false` and no value.
 */
export type NetworkScoreHabitKind = 'rate' | 'weeks' | 'count';

export interface INetworkScoreHabit {
    label: string;
    kind: NetworkScoreHabitKind;
    value?: number | null;
    maximum: number;
    hasEnoughData: boolean;
}

/** `GET participant/score/me/habits`. */
export interface INetworkScoreHabitsResponse {
    rollingWindowDays: number;
    habits: INetworkScoreHabit[];
}

/** `GET participant/score/me/lesson` — the lesson unlocked at the user's current level. */
export interface INetworkScoreLessonResponse {
    lessonNumber: number;
    title: string;
    readMinutes: number;
    unlockedAtLevel: ScoreLevelName;
    teaches: string;
    task: string;
}

/** A score event in "History". `group` is the rule group name, e.g. `FollowUp`. */
export interface INetworkScoreHistoryItem {
    id: string;
    label: string;
    group: string;
    points: number;
    occurredAt: string;
    /** True while the event still counts towards the rolling window total. */
    isInWindow: boolean;
    counterpartyName?: string | null;
    eventTitle?: string | null;
}

/** `GET participant/score/me/history?page=&pageSize=` — one page, newest first. */
export interface INetworkScoreHistoryResponse {
    items: INetworkScoreHistoryItem[];
    totalCount: number;
    page: number;
    pageSize: number;
    rollingWindowDays: number;
    /** Points inside the rolling window, summed live from the ledger. */
    windowPoints: number;
}

/** `GET participant/score/rules` — one row of "How you earn points". */
export interface INetworkScoreRule {
    group: string;
    label: string;
    points: number;
    capCount: number;
    /** `Event`, `Day`, `Week`, `Connection` or `Post`. */
    capWindow: string;
}

const RULE_GROUP_LABELS: Record<string, string> = {
    ShowUp: 'Show up',
    Meet: 'Meet',
    FollowUp: 'Follow up',
    Community: 'Community',
};

/** Display name for a rule group; unknown groups fall back to the API value so new ones still render. */
export const formatRuleGroup = (group: string): string => RULE_GROUP_LABELS[group] ?? group;

/** Cap column text, e.g. "1 per event" or "5 per day". */
export const formatRuleCap = (rule: Pick<INetworkScoreRule, 'capCount' | 'capWindow'>): string =>
    `${rule.capCount} per ${rule.capWindow.toLowerCase()}`;

/** Formats a habit's value, e.g. "38%", "7 of 12 weeks" or "2". Empty-data rates read "Not enough data yet". */
export const formatHabitValue = (habit: INetworkScoreHabit): string => {
    if (!habit.hasEnoughData || habit.value == null) return 'Not enough data yet';
    if (habit.kind === 'rate') return `${Math.round(habit.value * 100)}%`;
    if (habit.kind === 'weeks') return `${habit.value} of ${habit.maximum} weeks`;
    return `${habit.value}`;
};

/** 0-100 fill for a habit's bar; capped so a count above its target doesn't overflow. */
export const getHabitFillPercent = (habit: INetworkScoreHabit): number => {
    if (!habit.hasEnoughData || habit.value == null || habit.maximum <= 0) return 0;
    return Math.round(Math.min(1, habit.value / habit.maximum) * 100);
};
